import * as Colors from '@brightlayer-ui/colors';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import React from 'react';

const styles: Record<string, SxProps<Theme>> = {
    container: {
        width: '100%',
    },
    title: {
        mb: 2,
    },
    emptyStateContainer: {
        textAlign: 'left',
    },
    tableWrapper: {
        overflowX: 'auto',
    },
    table: {
        minWidth: 750,
        tableLayout: 'fixed',
    },
    headerCell: {
        height: '56px',
        fontWeight: 600,
        fontSize: '14px',
        lineHeight: '19px',
        color: Colors.black[500],
        borderBottom: `1px solid ${Colors.black[500]}1F`,
    },
    bodyRow: {
        cursor: 'pointer',
    },
    bodyCell: {
        height: '52px',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '19px',
        color: Colors.black[500],
        borderBottom: `1px solid ${Colors.black[500]}1F`,
    },
    emptyRowCell: {
        border: 0,
    },
    pagination: {
        '& .MuiTablePagination-select': {
            background: Colors.white[200],
            borderRadius: '4px 4px 0px 0px',
            borderBottom: `1px solid ${Colors.black[500]}1F`,
            color: Colors.black[500],
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '22px',
        },
        '& .MuiTablePagination-selectIcon': {
            color: Colors.gray[500],
        },
        '& .MuiTablePagination-displayedRows': {
            fontSize: '14px',
            fontWeight: 600,
            lineHeight: '19px',
            color: Colors.black[500],
        },
        '& .MuiTablePagination-selectLabel': {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '19px',
            color: Colors.black[500],
        },
        '& .MuiTablePagination-actions button.Mui-disabled': {
            color: `${Colors.black[500]}4D`,
        },
    },
};

type TableData = Record<string, any>;
type SortOrder = 'asc' | 'desc';

type Column = {
    field: string;
    headerName: string;
    width?: number;
    numeric?: boolean;
    customSort?: (a: any, b: any) => number;
    valueFormatter?: (value: any) => React.ReactNode;
};

type PaginatedTableProps = {
    dataset: TableData[];
    title?: string;
    columns?: Column[];
    rowsPerPageOptions?: number[];
    defaultRowsPerPage?: number;
    labelRowsPerPage?: string;
    'data-testid'?: string;
    customSortFunctions?: Record<string, (a: any, b: any) => number>;
    emptyStateMessage?: string;
    initialOrderBy?: string;
    initialOrder?: SortOrder;
};

type HeadCell = {
    id: string;
    label: string;
    numeric: boolean;
    disablePadding: boolean;
    customSort?: (a: any, b: any) => number;
    valueFormatter?: (value: any) => React.ReactNode;
};

type TableHeaderProps = {
    order: SortOrder;
    orderBy: string;
    onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
    headCells: HeadCell[];
};

const descendingComparator = <T extends TableData>(
    a: T,
    b: T,
    orderBy: keyof T,
    customSort?: (a: any, b: any) => number
): number => {
    if (customSort) {
        return customSort(a[orderBy], b[orderBy]);
    }

    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
};

const getComparator = <Key extends keyof any>(
    order: SortOrder,
    orderBy: Key,
    customSort?: (a: any, b: any) => number
): ((a: Record<Key, number | string>, b: Record<Key, number | string>) => number) =>
    order === 'desc'
        ? (a: Record<Key, number | string>, b: Record<Key, number | string>): number =>
              descendingComparator(a, b, orderBy, customSort)
        : (a: Record<Key, number | string>, b: Record<Key, number | string>): number =>
              -descendingComparator(a, b, orderBy, customSort);

const generateColumnsFromData = (
    data: TableData[],
    customColumns?: Column[],
    customSortFunctions?: Record<string, (a: any, b: any) => number>
): HeadCell[] => {
    if (customColumns) {
        return customColumns.map((col) => ({
            id: col.field,
            label: col.headerName,
            numeric: col.numeric ?? false,
            disablePadding: false,
            customSort: col.customSort ?? customSortFunctions?.[col.field],
            valueFormatter: col.valueFormatter,
        }));
    }

    if (data.length === 0) {
        return [];
    }

    return Object.keys(data[0]).map((key) => ({
        id: key,
        label: key.charAt(0).toUpperCase() + key.slice(1),
        numeric: typeof data[0][key] === 'number',
        disablePadding: false,
        customSort: customSortFunctions?.[key],
    }));
};

const TableHeader: React.FC<TableHeaderProps> = ({ order, orderBy, onRequestSort, headCells }) => {
    const createSortHandler =
        (property: string): ((event: React.MouseEvent<unknown>) => void) =>
        (event: React.MouseEvent<unknown>): void => {
            onRequestSort(event, property);
        };

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell: HeadCell) => (
                    <TableCell
                        key={headCell.id}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                        align={headCell.numeric ? 'right' : 'left'}
                        sx={styles.headerCell}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
};

export const PaginatedTableComponent: React.FC<PaginatedTableProps> = ({
    title,
    dataset,
    columns,
    rowsPerPageOptions = [5, 10, 25],
    defaultRowsPerPage = 5,
    labelRowsPerPage,
    'data-testid': dataTestId,
    customSortFunctions,
    emptyStateMessage,
    initialOrderBy = '',
    initialOrder = 'asc',
}) => {
    const [orderBy, setOrderBy] = React.useState(initialOrderBy);
    const [order, setOrder] = React.useState<SortOrder>(initialOrder);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);

    const headCells = React.useMemo(
        () => generateColumnsFromData(dataset, columns, customSortFunctions),
        [dataset, columns, customSortFunctions]
    );

    // Event handlers
    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string): void => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number): void => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - dataset.length) : 0;

    const visibleRows = React.useMemo(() => {
        const currentColumn = headCells.find((cell) => cell.id === orderBy);
        const customSort = currentColumn?.customSort;

        const sortedData = [...dataset].sort(getComparator(order, orderBy, customSort));
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return sortedData.slice(startIndex, endIndex);
    }, [dataset, order, orderBy, page, rowsPerPage, headCells]);

    return (
        <Box sx={styles.container} data-testid={dataTestId}>
            {title && (
                <Typography color={Colors.gray[800]} sx={styles.title} variant="h6" id="tableTitle" component="div">
                    {title}
                </Typography>
            )}

            {dataset.length === 0 ? (
                <Box sx={styles.emptyStateContainer}>
                    <Typography color={Colors.black[500]} variant="body2">
                        {emptyStateMessage}
                    </Typography>
                </Box>
            ) : (
                <Box sx={styles.tableWrapper}>
                    <TableContainer>
                        <Table sx={styles.table} aria-labelledby="tableTitle" size="medium">
                            <TableHeader
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                headCells={headCells}
                            />
                            <TableBody>
                                {visibleRows.map((row, index) => {
                                    const labelId = `enhanced-table-row-${index}`;
                                    const rowKey = row.id ?? index;

                                    return (
                                        <TableRow hover tabIndex={-1} key={rowKey} sx={styles.bodyRow}>
                                            {headCells.map((cell, colIdx) => (
                                                <TableCell
                                                    key={cell.id}
                                                    component={colIdx === 0 ? 'th' : undefined}
                                                    id={colIdx === 0 ? labelId : undefined}
                                                    scope={colIdx === 0 ? 'row' : undefined}
                                                    padding="normal"
                                                    align={'left'}
                                                    sx={styles.bodyCell}
                                                >
                                                    {cell.valueFormatter
                                                        ? cell.valueFormatter(row[cell.id])
                                                        : row[cell.id]}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })}

                                {emptyRows > 0 && (
                                    <TableRow style={{ height: 53 * emptyRows }}>
                                        <TableCell colSpan={headCells.length || 1} sx={styles.emptyRowCell} />
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={rowsPerPageOptions}
                        component="div"
                        count={dataset.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage={labelRowsPerPage}
                        showFirstButton
                        showLastButton
                        sx={styles.pagination}
                    />
                </Box>
            )}
        </Box>
    );
};
